import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { useGT } from 'gt-react';

const KEYBOARD_STEP_PX = 16;

/**
 * A thin drag strip on the panel's left edge. Pointer capture keeps the drag
 * alive when the cursor leaves the strip; arrow keys resize for keyboard users.
 * `onResize` receives the width delta (dragging left widens the panel).
 */
export function NamiResizeHandle({
  onResize,
}: {
  onResize: (deltaPx: number) => void;
}) {
  const gt = useGT();
  const lastXRef = useRef<number | null>(null);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    lastXRef.current = event.clientX;
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (lastXRef.current === null) return;
    const dx = event.clientX - lastXRef.current;
    lastXRef.current = event.clientX;
    if (dx !== 0) onResize(-dx);
  };

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (lastXRef.current === null) return;
    lastXRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onResize(KEYBOARD_STEP_PX);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onResize(-KEYBOARD_STEP_PX);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={gt('Resize assistant panel', {
        context:
          'accessible label of the drag handle that resizes the AI assistant panel',
      })}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onKeyDown={handleKeyDown}
      className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize hover:bg-border focus-visible:bg-border focus-visible:outline-none active:bg-border"
    />
  );
}
